import React, {useMemo} from 'react';
import {ScoringSignal} from '../../shared/signals';
import {useActiveMarket} from '../../hooks/api/metadata';
import {useScoringSignalResolver} from '../../hooks/scoringSignal';
import {
  Box,
  Flex,
  PopoverTrigger,
  Popover,
  PopoverHeader,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import {RawSignal} from '../signals';
import {FitSignalsSection} from './AccountGrid.details';

const _FitSignalsWithPopover = ({
  signals,
  maxCharacters,
}: {
  signals: ScoringSignal[];
  maxCharacters: number;
}) => {
  const market = useActiveMarket();
  const scoringSignalResolver = useScoringSignalResolver();

  const adjustedSignals = useMemo(() => {
    // Sort signals by scoring model weights
    const sortedSignals = signals
      .map((signal) => {
        const resolvedSignal = scoringSignalResolver(signal);
        if (!resolvedSignal) {
          return null;
        }

        const weight =
          market?.scoringModel.signalsWithWeights.find(
            (s) => s.signal === signal
          )?.weight ?? 1;

        return {signal: resolvedSignal, weight};
      })
      .filter((signal) => signal !== null)
      .sort((a, b) => b.weight - a.weight)
      .map(({signal}) => signal);

    let runningTotal = 0;
    const trimmedSignals = [];
    // Only show the top signals that fit within the max characters limit
    for (const signal of sortedSignals) {
      const {label} = signal;
      if (runningTotal + label.length > maxCharacters) {
        break;
      }

      runningTotal += label.length;
      trimmedSignals.push(signal);
    }

    return trimmedSignals;
  }, [signals, market, maxCharacters, scoringSignalResolver]);

  const hasMoreSignals = signals.length > adjustedSignals.length;
  const content = (
    <Flex alignItems="baseline" wrap="wrap" gap={2}>
      {adjustedSignals.map((signal) => (
        <RawSignal
          category={signal.category}
          label={signal.label}
          key={signal.id}
          variant="outline"
        />
      ))}
      {hasMoreSignals && (
        <Box color="kgray.300" fontSize="xs">
          + {signals.length - adjustedSignals.length} more
        </Box>
      )}
    </Flex>
  );

  if (!hasMoreSignals) {
    return content;
  }

  return (
    <Popover isLazy={true} placement="auto" trigger="hover">
      <PopoverTrigger>{content}</PopoverTrigger>
      <PopoverContent p={2}>
        <PopoverArrow />
        <PopoverHeader border={0} fontWeight={500}>
          Fit Signals
        </PopoverHeader>
        <PopoverBody maxH="50vh" overflowY="auto">
          <FitSignalsSection signals={signals} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const FitSignalsWithPopover = React.memo(_FitSignalsWithPopover);
